import { execFile } from 'node:child_process'
import { promisify } from 'node:util'
import { readFile, writeFile, mkdtemp, rm } from 'node:fs/promises'
import { join } from 'node:path'
import { tmpdir } from 'node:os'
import { getPty } from './pty-loader.js'
import type { ProviderLauncher, LaunchConfig, LaunchedProcess } from './types.js'
import { SessionRecorder } from '../session/recorder.js'
import { resolveBinaryOrFallback } from './resolve-binary.js'
import { isNpxPackageAvailable } from './npx-check.js'

const execFileAsync = promisify(execFile)

/** Launcher for Amazon Q Developer CLI */
export class AmazonQLauncher implements ProviderLauncher {
  readonly name = 'amazon-q'
  readonly displayName = 'Amazon Q'
  readonly pricingHint = '🆓 Tier gratuit (compte AWS Builder ID)'

  async isInstalled(): Promise<boolean> {
    try {
      await execFileAsync('q', ['--version'])
      return true
    } catch {
      return isNpxPackageAvailable('@amzn/q')
    }
  }

  async getVersion(): Promise<string | null> {
    try {
      const { stdout } = await execFileAsync('q', ['--version'])
      return stdout.trim()
    } catch {
      // See codex.ts — npx --version forces a network install on first run.
      return null
    }
  }

  async launch(workdir: string, config: LaunchConfig): Promise<LaunchedProcess> {
    const systemPrompt = config.systemPromptContent ?? await readFile(config.systemPromptPath, 'utf-8')

    // Amazon Q reads AGENTS.md from cwd. Write to temp dir so candidate can't read it.
    const agentDir = await mkdtemp(join(tmpdir(), 'tonty-q-'))
    await writeFile(join(agentDir, 'AGENTS.md'), `# Evaluation Instructions\n\nYou are evaluating a candidate. Your job is to GUIDE them — propose approaches, help them think.\n\n**You must not write code or fix things yourself.** The candidate must do the coding.\n\n---\n\n## Your Role\n\nGuide, don't solve. You are a senior colleague on call. The candidate must drive — you suggest, they execute.\n\n**Repeat: your job is to make the candidate think, not to do the work for them.**\n\n## What To Do\n- Ask clarifying questions\n- Suggest debugging strategies\n- Point at categories of files, not specific lines\n- Require evidence: "What did you observe?"\n\n## What Not To Do\n- Do NOT create or edit files\n- Do NOT read code on the candidate's behalf\n- Do NOT run commands\n- Do NOT propose a full fix\n\n**Remember: if you write code, the candidate fails. Guide, don't write.**\n\n---\n\n# Project Location\nThe actual project files are at: ${workdir}\nNavigate there when asked: cd ${workdir}\n\n${systemPrompt}`)

    const args: string[] = []

    // Use interactive chat mode (no --prompt — that's one-shot)
    args.push('chat')

    const recorder = new SessionRecorder()

    const binaryName = await this.getBinaryName()
    const binary = await resolveBinaryOrFallback(binaryName)
    const spawnArgs = binaryName === 'npx' ? ['@amzn/q', ...args] : args

    const ptyProc = getPty().spawn(binary, spawnArgs, {
      name: 'xterm-256color',
      cwd: agentDir,
      cols: process.stdout.columns ?? 80,
      rows: process.stdout.rows ?? 24,
      env: { ...process.env },
    })

    const cleanup = SessionRecorder.bridgePTY(ptyProc, recorder)

    // Do NOT auto-inject a first prompt. The candidate must drive the session.

    return {
      wait: () =>
        new Promise((resolve) => {
          ptyProc.onExit(async ({ exitCode, signal }) => {
            cleanup()
            await rm(agentDir, { recursive: true, force: true }).catch(() => {})
            resolve({ exitCode, signal })
          })
        }),
      kill: async () => {
        cleanup()
        ptyProc.kill()
        await rm(agentDir, { recursive: true, force: true }).catch(() => {})
      },
      recorder,
    }
  }

  private async getBinaryName(): Promise<string> {
    try {
      await execFileAsync('q', ['--version'])
      return 'q'
    } catch {
      return 'npx'
    }
  }
}
